'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { X, ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX } from 'lucide-react'
import { useTheme } from '../theme/provider'

export interface StoryItem {
  id: string | number
  type: 'image' | 'video'
  url: string
  duration?: number // in milliseconds
  thumbnail?: string
  caption?: string
  link?: {
    url: string
    text: string
  }
}

export interface StoryCollection {
  id: string | number
  title: string
  thumbnail: string
  author?: {
    name: string
    avatar?: string
  }
  timestamp?: string
  items: StoryItem[]
  isViewed?: boolean
}

export interface StoryViewerProps {
  stories: StoryCollection[]
  initialStoryIndex?: number
  initialItemIndex?: number
  onClose: () => void
  onStoryChange?: (storyIndex: number, itemIndex: number) => void
  onComplete?: () => void
}

/**
 * StoryViewer Component - Instagram/Facebook style story viewer
 * Fullscreen modal for viewing stories with progress bars and navigation
 *
 * Features:
 * - Fullscreen immersive experience
 * - Progress bars for each story item
 * - Auto-advance with configurable duration
 * - Tap left/right or swipe for navigation
 * - Pause/play controls
 * - Video support with sound toggle
 * - Story information (author, timestamp)
 * - Link/CTA buttons
 * - Keyboard navigation (arrow keys, escape)
 *
 * @example
 * ```tsx
 * <StoryViewer
 *   stories={storyCollections}
 *   initialStoryIndex={0}
 *   onClose={() => setShowStories(false)}
 * />
 * ```
 */
export function StoryViewer({
  stories,
  initialStoryIndex = 0,
  initialItemIndex = 0,
  onClose,
  onStoryChange,
  onComplete,
}: StoryViewerProps) {
  const { theme } = useTheme()
  const [currentStoryIndex, setCurrentStoryIndex] = useState(initialStoryIndex)
  const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex)
  const [progress, setProgress] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  const currentStory = stories[currentStoryIndex]
  const currentItem = currentStory?.items[currentItemIndex]
  const duration = currentItem?.duration || 5000 // Default 5 seconds

  // Progress animation
  useEffect(() => {
    if (!currentItem || isPaused) return

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext()
          return 0
        }
        return prev + (100 / (duration / 50))
      })
    }, 50)

    return () => clearInterval(interval)
  }, [currentStoryIndex, currentItemIndex, isPaused, duration])

  // Navigate to next item/story
  const goToNext = useCallback(() => {
    if (currentItemIndex < currentStory.items.length - 1) {
      // Next item in current story
      setCurrentItemIndex((prev) => prev + 1)
      setProgress(0)
      onStoryChange?.(currentStoryIndex, currentItemIndex + 1)
    } else if (currentStoryIndex < stories.length - 1) {
      // Next story
      setCurrentStoryIndex((prev) => prev + 1)
      setCurrentItemIndex(0)
      setProgress(0)
      onStoryChange?.(currentStoryIndex + 1, 0)
    } else {
      // Completed all stories
      onComplete?.()
      onClose()
    }
  }, [currentStoryIndex, currentItemIndex, currentStory, stories.length, onStoryChange, onComplete, onClose])

  // Navigate to previous item/story
  const goToPrevious = useCallback(() => {
    if (currentItemIndex > 0) {
      // Previous item in current story
      setCurrentItemIndex((prev) => prev - 1)
      setProgress(0)
      onStoryChange?.(currentStoryIndex, currentItemIndex - 1)
    } else if (currentStoryIndex > 0) {
      // Previous story (last item)
      const prevStoryIndex = currentStoryIndex - 1
      const prevStoryLastItemIndex = stories[prevStoryIndex].items.length - 1
      setCurrentStoryIndex(prevStoryIndex)
      setCurrentItemIndex(prevStoryLastItemIndex)
      setProgress(0)
      onStoryChange?.(prevStoryIndex, prevStoryLastItemIndex)
    }
  }, [currentStoryIndex, currentItemIndex, stories, onStoryChange])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') goToNext()
      if (e.key === 'ArrowLeft') goToPrevious()
      if (e.key === ' ') {
        e.preventDefault()
        setIsPaused((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrevious, onClose])

  if (!currentStory || !currentItem) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black"
      style={{ backgroundColor: '#000000' }}
    >
      {/* Story Content */}
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Background Image/Video */}
        {currentItem.type === 'image' ? (
          <Image
            src={currentItem.url}
            alt={currentItem.caption || 'Story'}
            fill
            className="object-contain"
            sizes="100vw"
            priority
          />
        ) : (
          <video
            src={currentItem.url}
            className="w-full h-full object-contain"
            autoPlay
            loop
            muted={isMuted}
            playsInline
          />
        )}

        {/* Gradient Overlays */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Progress Bars */}
        <div className="absolute top-4 left-4 right-4 flex gap-1">
          {currentStory.items.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <div
                className="h-full bg-white transition-all duration-100"
                style={{
                  width:
                    index < currentItemIndex
                      ? '100%'
                      : index === currentItemIndex
                      ? `${progress}%`
                      : '0%',
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-8 left-4 right-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Author Avatar */}
            {currentStory.author?.avatar && (
              <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-white">
                <Image
                  src={currentStory.author.avatar}
                  alt={currentStory.author.name}
                  fill
                  className="object-cover"
                  sizes="40px"
                />
              </div>
            )}

            {/* Author Info */}
            <div>
              <p className="text-white font-semibold text-sm">
                {currentStory.author?.name || currentStory.title}
              </p>
              {currentStory.timestamp && (
                <p className="text-white/80 text-xs">{currentStory.timestamp}</p>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Pause/Play */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
            >
              {isPaused ? (
                <Play className="w-4 h-4 text-white" />
              ) : (
                <Pause className="w-4 h-4 text-white" />
              )}
            </button>

            {/* Mute/Unmute (for videos) */}
            {currentItem.type === 'video' && (
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-white" />
                ) : (
                  <Volume2 className="w-4 h-4 text-white" />
                )}
              </button>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Navigation Areas (Tap left/right) */}
        <button
          onClick={goToPrevious}
          className="absolute left-0 top-0 bottom-0 w-1/3 flex items-center justify-start p-8 group"
          disabled={currentStoryIndex === 0 && currentItemIndex === 0}
        >
          <ChevronLeft className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-0 top-0 bottom-0 w-1/3 flex items-center justify-end p-8 group"
        >
          <ChevronRight className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>

        {/* Caption & Link */}
        {(currentItem.caption || currentItem.link) && (
          <div className="absolute bottom-8 left-4 right-4">
            {currentItem.caption && (
              <p className="text-white text-sm mb-3 drop-shadow-lg">
                {currentItem.caption}
              </p>
            )}
            {currentItem.link && (
              <a
                href={currentItem.link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: theme.colors.primary,
                  color: '#ffffff',
                }}
              >
                {currentItem.link.text}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
